import { FC, useCallback, useEffect, useReducer, useState } from "react";
import { StorageKeys } from "~/application/constants";
import { authService } from "~/application/usecases";
import { snackbarService } from "~/components/SnackbarStack";
import { api } from "~/infrastructure/api";
import { queryClient } from "~/services/queryClient";
import { AuthContext, AuthSignInData, SignInParams } from "../../contexts/AuthContext";
import { AuthDispatchAction, AuthProviderProps, AuthState } from "./types";
import { useNavigate } from "react-router";

export function authReducer(state: AuthState, action: AuthDispatchAction): AuthState {
  switch (action.type) {
    case "SIGN_IN":
      return {
        ...state,
        user: action.payload.user,
        authToken: action.payload.authToken,
        isLoading: false,
      };
    case "SIGN_OUT":
      return {
        ...state,
        user: null!,
        authToken: undefined,
        isLoading: false,
      };
    default:
      return state;
  }
}

const initialState: AuthState = {
  user: null,
  authToken: undefined,
  isLoading: true,
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [{ user, authToken, isLoading }, dispatch] = useReducer(authReducer, initialState);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null | undefined>();
  const [attempts, setAttempts] = useState(() => {
    const data = localStorage.getItem("loginAttemptsData");
    if (data) {
      const parsedData = JSON.parse(data);
      return Number(parsedData.attempts);
    }
    return 0;
  });

  const navigate = useNavigate();

  const getLoginAttemptsData = (): Record<string, { attempts: number; blocked: boolean }> => {
    const data = localStorage.getItem("loginAttemptsData");
    return data ? JSON.parse(data) : {};
  };

  const saveLoginAttemptsData = (data: Record<string, { attempts: number; blocked: boolean }>) => {
    localStorage.setItem("loginAttemptsData", JSON.stringify(data));
  };

  const saveAuthToken = useCallback(
    ({ authToken, user, storeTokenLocally }: AuthSignInData) => {
      api.headers.common["Authorization"] = `Bearer ${authToken.value}`;

      if (!authToken.createdAt) return;

      const dateToken = authToken.createdAt.getTime();
      localStorage.setItem("user", JSON.stringify(user));
      if (storeTokenLocally) {
        localStorage.setItem(StorageKeys.AUTH_TOKEN, authToken.value || "");
        localStorage.setItem(StorageKeys.DATE_TOKEN, dateToken.toString());
      } else {
        sessionStorage.setItem(StorageKeys.AUTH_TOKEN, authToken.value || "");
      }

      dispatch({
        type: "SIGN_IN",
        payload: {
          authToken,
          user,
        },
      });
    },
    [dispatch]
  );

  const signIn = useCallback(
    async (
      { email, password, keepSigned = false, securityKey }: SignInParams,
      onErrorAttempt?: () => void
    ) => {
      const loginData = getLoginAttemptsData();
      const userData = loginData[email] || { attempts: 0, blocked: false };

      const resetLoginAttempts = () => {
        delete loginData[email];
        saveLoginAttemptsData(loginData);
        setAttempts(0);
      };

      try {
        const { authToken, user } = await authService.signIn({
          email,
          password,
          secret: securityKey.replace(/\s+/g, ""),
        });

        resetLoginAttempts();

        saveAuthToken({ authToken, user, storeTokenLocally: keepSigned });
        setQrCodeUrl(undefined);

        // Redireciona para a última rota acessada antes do logout/401, se existir
        try {
          const redirectTo =
            sessionStorage.getItem(StorageKeys.REDIRECT_TO) ||
            localStorage.getItem(StorageKeys.REDIRECT_TO);

          if (redirectTo && !redirectTo.startsWith("/entrar")) {
            sessionStorage.removeItem(StorageKeys.REDIRECT_TO);
            localStorage.removeItem(StorageKeys.REDIRECT_TO);
            navigate(redirectTo, { replace: true });
          } else {
            navigate("/", { replace: true });
          }
        } catch {
          navigate("/", { replace: true });
        }
      } catch (error: any) {
        // Tratamento de erro da API
        if (error.data?.errors) {
          (Object.values(error.data.errors) as string[]).forEach((error) =>
            snackbarService.showSnackbar(error, "error")
          );
          return;
        }

        if (error?.code === "MANDATORY_SECRET_KEY") {
          const { qrCodeUrl } = await authService.generateSecretKey({ email, password });
          setQrCodeUrl(qrCodeUrl);
          return;
        }

        if (error?.message) {
          snackbarService.showSnackbar(error.message, "error");
        } else {
          snackbarService.showSnackbar("Erro desconhecido", "error");
        }

        userData.attempts += 1;

        if (userData.attempts >= 5) {
          resetLoginAttempts();
        } else {
          loginData[email] = userData;
          saveLoginAttemptsData(loginData);
          setAttempts(userData.attempts);
        }

        if (onErrorAttempt) {
          onErrorAttempt();
        }
      }
    },

    [saveAuthToken, navigate]
  );

  const signOut = useCallback(() => {
    delete api.headers.common["Authorization"];
    sessionStorage.removeItem(StorageKeys.AUTH_TOKEN);
    localStorage.removeItem(StorageKeys.AUTH_TOKEN);
    localStorage.removeItem(StorageKeys.DATE_TOKEN);
    localStorage.removeItem(StorageKeys.AGENCY_CUSTOMER_ID);

    queryClient.clear();
    dispatch({ type: "SIGN_OUT" });
  }, [dispatch]);

  useEffect(() => {
    async function loadDataAsync() {
      const authToken =
        localStorage.getItem(StorageKeys.AUTH_TOKEN) ||
        sessionStorage.getItem(StorageKeys.AUTH_TOKEN);
      const dateToken = localStorage.getItem(StorageKeys.DATE_TOKEN);

      if (authToken && dateToken) {
        const { user } = await authService.verifyToken({
          value: authToken,
          createdAt: new Date(parseInt(dateToken)),
        });

        saveAuthToken({
          user,
          authToken: {
            value: authToken,
            createdAt: new Date(parseInt(dateToken)),
          },
        });
      } else {
        signOut();
      }
    }

    loadDataAsync();
  }, [signOut, saveAuthToken]);

  const isAuthenticationPage =
    new URLSearchParams(window.location.search).get("authenticacao") === "2fa";

  return (
    <AuthContext.Provider
      value={{
        user: user!,
        authToken: authToken!,
        isSigned: !!user,
        isLoading,
        saveAuthToken,
        signIn,
        signOut,
        qrCodeUrl,
        attempts: isAuthenticationPage ? 0 : attempts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
